import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import SaveIcon from '@mui/icons-material/Save'
import DeleteIcon from '@mui/icons-material/Delete'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { getSystemConfig } from '../graphql/queries'
import { deleteSystemConfig, updateSystemConfig } from '../graphql/mutation'

import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  MenuItem,
  Select,
  FormControl,
  Snackbar,
  CircularProgress,
  Paper,
  InputLabel,
} from '@mui/material'

const providerOptions = [
  { value: 'FAL', name: 'FAL' },
  { value: 'WIRO', name: 'WIRO' },
  { value: 'SERVICE', name: 'SERVICE' },
]

const toolTypeOptions = [
  { value: 'IMG2IMG', name: 'IMG2IMG' },
  { value: 'IMG2VIDEO', name: 'IMG2VIDEO' },
  { value: 'IMG2VIDEO_WITH_TEXT', name: 'IMG2VIDEO_WITH_TEXT' },
  { value: 'PREDICT_WITH_MODEL', name: 'PREDICT_WITH_MODEL' },
  { value: 'TEXT2VIDEO', name: 'TEXT2VIDEO' },
  { value: 'TRAIN', name: 'TRAIN' },
  { value: 'VIDEO2VIDEO', name: 'VIDEO2VIDEO' },
]

export default function SystemPage() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [systemConfig, setSystemConfig] = useState({
    provider: '',
    systemType: '',
    systemModel: '',
  })

  const [progress, setProgress] = useState(false)
  const [snackBarOpen, setSnackBarOpen] = useState(false)
  const [snackBarMessage, setSnackBarMessage] = useState(null)
  const [snackBarSeverity, setSnackBarSeverity] = useState('success')

  const { loading, error, data, refetch } = useQuery(getSystemConfig, {
    variables: { id },
    onCompleted: data => {
      if (data.getSystemConfig) {
        setSystemConfig({
          provider: data.getSystemConfig.provider,
          systemType: data.getSystemConfig.systemType,
          systemModel: data.getSystemConfig.systemModel || '',
        })
      }
    },
  })

  const [updateSystemConfigMutation] = useMutation(updateSystemConfig)
  const [deleteSystemConfigMutation] = useMutation(deleteSystemConfig)

  const handleChange = e => {
    const { name, value } = e.target
    setSystemConfig(prev => ({ ...prev, [name]: value }))
  }

  const handleSnackBarClose = () => {
    setSnackBarOpen(false)
  }

  const handleUpdateSystemConfig = async () => {
    setProgress(true)
    try {
      const response = await updateSystemConfigMutation({
        variables: {
          id,
          provider: systemConfig.provider,
          systemType: systemConfig.systemType,
          systemModel: systemConfig.systemModel || null,
        },
      })
      
      if (response.data) {
        setSnackBarMessage('System configuration updated successfully')
        setSnackBarSeverity('success')
        setSnackBarOpen(true)
        refetch()
      }
    } catch (error) {
      console.error('Error updating system config:', error)
      setSnackBarMessage(`Update failed: ${error.message}`)
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
    } finally {
      setProgress(false)
    }
  }

  const handleDeleteSystemConfig = async () => {
    if (!window.confirm('Are you sure you want to delete this system configuration?')) {
      return
    }

    try {
      setProgress(true)
      const response = await deleteSystemConfigMutation({
        variables: { id },
      })
      
      if (response.data) {
        setSnackBarMessage('System configuration deleted successfully')
        setSnackBarSeverity('success')
        setSnackBarOpen(true)
        setTimeout(() => {
          navigate('/system')
        }, 1000)
      }
    } catch (error) {
      console.error('Delete error:', error)
      setSnackBarMessage(`Delete failed: ${error.message}`)
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
    } finally {
      setProgress(false)
    }
  }

  if (loading) return <CircularProgress />
  if (error) return <Typography color="error">Error: {error.message}</Typography>

  return (
    <Box>
      <Snackbar
        open={snackBarOpen}
        autoHideDuration={3000}
        onClose={handleSnackBarClose}
        message={snackBarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackBarClose} 
          severity={snackBarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackBarMessage}
        </Alert>
      </Snackbar>

      {/* Sticky Header with Actions */}
      <Box 
        sx={{ 
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          backgroundColor: 'white',
          borderBottom: '1px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 4, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant='h4' sx={{ color: '#212529', fontWeight: 'bold' }}>
            System Configuration Details
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              onClick={handleDeleteSystemConfig}
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              sx={{ 
                borderRadius: 2,
                px: 3,
                '&:hover': { 
                  backgroundColor: '#e74c3c', 
                  color: 'white',
                  borderColor: '#e74c3c'
                }
              }}
            >
              Delete Configuration
            </Button>
            <Button
              onClick={handleUpdateSystemConfig}
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              sx={{ 
                borderRadius: 2,
                px: 4,
                backgroundColor: '#3498db',
                '&:hover': { backgroundColor: '#2980b9' }
              }}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </Box>

      {progress && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255,255,255,0.7)',
            zIndex: 1300,
          }}
        >
          <CircularProgress size={80} thickness={5} />
        </Box>
      )}

      {/* Main Content Container */}
      <Box sx={{ maxWidth: '1400px', mx: 'auto', px: { xs: 2, md: 4 } }}>
        {/* Basic Information Section */}
        <Paper sx={{ p: 4, mb: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: '#2c3e50', borderBottom: '2px solid #3498db', pb: 1 }}>
            System Configuration
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Provider</InputLabel>
              <Select 
                name="provider" 
                onChange={handleChange} 
                value={systemConfig?.provider ?? ''}
                label="Provider"
                sx={{ borderRadius: 2 }}
              >
                {providerOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>System Type</InputLabel>
              <Select 
                name="systemType" 
                onChange={handleChange} 
                value={systemConfig?.systemType ?? ''}
                label="System Type"
                sx={{ borderRadius: 2 }}
              >
                {toolTypeOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TextField
            fullWidth
            label="System Model"
            variant="outlined"
            name="systemModel"
            value={systemConfig?.systemModel ?? ''}
            onChange={handleChange}
            placeholder="Enter system model name (optional)"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            helperText="Optional field for specifying the system model"
          />
        </Paper>

        {/* Information Section */}
        <Paper sx={{ p: 4, mb: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: '#2c3e50', borderBottom: '2px solid #e74c3c', pb: 1 }}>
            Configuration Information
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#34495e', mb: 1 }}>
                Created At
              </Typography>
              <Typography variant="body1" sx={{ color: '#2c3e50' }}>
                {data?.getSystemConfig?.createdAt ? new Date(data.getSystemConfig.createdAt).toLocaleString() : 'N/A'}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#34495e', mb: 1 }}>
                Last Updated
              </Typography>
              <Typography variant="body1" sx={{ color: '#2c3e50' }}>
                {data?.getSystemConfig?.updatedAt ? new Date(data.getSystemConfig.updatedAt).toLocaleString() : 'N/A'}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  )
}
